import { useEffect, useRef, useState } from 'react';
import { cn } from '@workspace/ui/lib/utils';
import { Icon } from './Icon';

type Props = React.ComponentProps<typeof Icon> & {
  missingMode?: 'fallback' | 'hide';
  fallback?: string;
};

export const SafeIcon = ({
  name,
  className,
  missingMode = 'fallback',
  fallback = 'fallback',
  ...rest
}: Props) => {
  const ref = useRef<SVGSVGElement>(null);
  const [resolved, setResolved] = useState(name);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    setResolved(name);
    setHidden(false);

    const el = ref.current;
    if (!el) return;

    const id = requestAnimationFrame(() => {
      try {
        const box = el.getBBox();
        const empty = box.width === 0 && box.height === 0;

        if (empty) {
          if (missingMode === 'fallback') {
            setResolved(fallback);
          } else {
            setHidden(true);
          }
        }
      } catch {
        if (missingMode === 'fallback') setResolved(fallback);
        else setHidden(true);
      }
    });

    return () => cancelAnimationFrame(id);
  }, [name, missingMode, fallback]);

  if (hidden) return null;

  return (
    <Icon
      ref={ref}
      name={resolved}
      className={cn(className)}
      aria-label={resolved}
      {...rest}
    />
  );
};
